import React, { useState, useEffect } from "react";
import { SparklesIcon, CogIcon } from "@heroicons/react/24/outline";
import type { TechnicalSolutionPhaseProps } from "./technicalSolutionPhase.types";
import { 
  TechStackSelector, 
  ArchitectureVisualizer, 
  TechnicalConsiderations,
  TechnicalSolutionSkeleton,
  TechnicalDescriptionEditor
} from "./components";
import { useGeminiMutations } from "../../../../hooks/data/useProjectMutations";
import { Button } from "@/components";
import type { TechnicalSolution } from "@/features/Projects";
import { TechnicalInsights } from "../shared/TechnicalInsights";

// Default technical solution structure
const getDefaultTechnicalSolution = (): TechnicalSolution => ({
  description: "", // Start with empty description
  recommendedStack: {
    frontend: [],
    backend: [],
    database: [],
    infrastructure: [],
    devops: [],
  },
  architecture: {
    pattern: "Arquitetura em Camadas",
    description: "",
    components: [],
    integrations: [],
  },
  considerations: {
    performance: [],
    security: [],
    scalability: [],
    maintenance: [],
  },
  complexity: {
    overall: "medium",
    frontend: 5,
    backend: 5,
    devops: 5,
  },
  justifications: {},
});

export default function TechnicalSolutionPhase({ 
  project, 
  onSave, 
  onNext 
}: TechnicalSolutionPhaseProps) {
  const [technicalSolution, setTechnicalSolution] = useState<TechnicalSolution>(
    project.technicalSolution || getDefaultTechnicalSolution()
  );
  
  // Check if we have advanced content to determine initial editor mode
  const initialHasAdvancedContent = (project.technicalSolution?.recommendedStack?.frontend?.length || 0) > 0 ||
    (project.technicalSolution?.recommendedStack?.backend?.length || 0) > 0 ||
    (project.technicalSolution?.architecture?.components?.length || 0) > 0;
    
  const [showAdvancedEditor, setShowAdvancedEditor] = useState(initialHasAdvancedContent);
  const { generateTechnicalSolution, isGeneratingTechnicalSolution } = useGeminiMutations();

  // Handle saving the technical description (first step)
  const handleSaveTechnicalDescription = async (description: string) => {
    const updatedSolution = { ...technicalSolution, description };
    setTechnicalSolution(updatedSolution);
    try {
      await onSave(updatedSolution);
    } catch (error) {
      console.error("❌ Erro ao salvar descrição técnica:", error);
      throw error;
    }
  };

  // Handle moving to advanced editor (after description is saved)
  const handleMoveToAdvancedEditor = () => {
    setShowAdvancedEditor(true);
  };

  // Handle saving the complete technical solution (advanced editor) and continue to next phase
  const handleSaveAdvancedSolution = async (solution: TechnicalSolution) => {
    setTechnicalSolution(solution);
    await onSave(solution);
    onNext(); // Navigate to next phase (milestones)
  };

  // Generate complete technical solution from description using AI
  const handleGenerateFromDescription = async () => {
    if (!technicalSolution.description) {
      return;
    }

    try {
      // Pass the technical description as the main description and project context
      const generatedSolution = await generateTechnicalSolution({
        projectId: project.id,
        description: technicalSolution.description, // Use technical description as main input
        objectives: project.objectives || [],
        projectContext: {
          projectDescription: project.description, // Pass original project description as context
          projectName: project.name,
        },
      });
      
      // Keep the existing description and merge with AI-generated details
      const updatedSolution = {
        ...generatedSolution,
        description: technicalSolution.description, // Preserve user's technical description
      };
      
      setTechnicalSolution(updatedSolution);
      // Save the updated solution with preserved description
      await onSave(updatedSolution);
      setShowAdvancedEditor(true);
    } catch (error) {
      console.error("Erro ao gerar solução técnica:", error);
    }
  };

  const hasDescription = technicalSolution?.description && technicalSolution?.description?.trim().length > 0;
  const hasAdvancedContent = technicalSolution?.recommendedStack?.frontend?.length > 0 ||
    technicalSolution?.recommendedStack?.backend?.length > 0 ||
    technicalSolution?.architecture?.components?.length > 0;

  if (isGeneratingTechnicalSolution) {
    return <TechnicalSolutionSkeleton />;
  }

  // Show the technical description editor when: 1) no description exists, or 2) we're not in advanced mode
  if (!showAdvancedEditor) {
    return (
      <div className="space-y-4">
        {/* Combined Header and Editor */}
        <div className="bg-white rounded-lg border border-gray-200">
          {/* Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <CogIcon className="h-5 w-5 text-blue-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Solução Técnica</h2>
                <p className="text-sm text-gray-600">
                  Descreva a abordagem técnica do projeto ou use a IA para gerar uma descrição completa.
                </p>
              </div>
            </div>
          </div>

          {/* Technical Description Editor */}
          <div className="p-4">
            <TechnicalDescriptionEditor
              project={project}
              initialDescription={technicalSolution.description || ""}
              onSave={handleSaveTechnicalDescription}
              onNext={handleMoveToAdvancedEditor}
            />
          </div>
        </div>

        {/* Generate Advanced Solution Option - More compact */}
        {hasDescription && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg">
                <SparklesIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-md font-medium text-gray-900 mb-1">
                  Gerar Detalhes Técnicos com IA
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  A IA pode gerar automaticamente a stack tecnológica, arquitetura e considerações técnicas.
                </p>
                <div className="flex space-x-2">
                  <Button
                    onClick={handleGenerateFromDescription}
                    disabled={isGeneratingTechnicalSolution}
                    leftIcon={<SparklesIcon className="h-4 w-4" />}
                    size="sm"
                  >
                    {isGeneratingTechnicalSolution ? "Gerando..." : "Gerar Detalhes Técnicos"}
                  </Button>
                  <Button
                    onClick={handleMoveToAdvancedEditor}
                    variant="secondary"
                    size="sm"
                  >
                    Definir Manualmente
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Advanced editor mode - show all technical components
  return (
    <div className="space-y-4">
      {/* Header with back option - more compact */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CogIcon className="h-5 w-5 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Detalhes da Solução Técnica</h2>
              <p className="text-sm text-gray-600">Configure a stack tecnológica, arquitetura e considerações técnicas.</p>
            </div>
          </div>
          
          <Button
            onClick={() => setShowAdvancedEditor(false)}
            variant="secondary"
            size="sm"
          >
            ← Voltar para Descrição
          </Button>
        </div>

        {/* Technical Description Summary - inline */}
        {hasDescription && (
          <div className="mt-4 bg-gray-50 rounded-lg p-3 border-l-4 border-blue-500">
            <h4 className="text-xs font-medium text-gray-900 mb-1">Descrição Técnica:</h4>
            <p className="text-sm text-gray-700 line-clamp-2">
              {technicalSolution.description || "Nenhuma descrição técnica definida"}
            </p>
          </div>
        )}
      </div>

      {/* Main Content - Single Column Stacked Layout */}
      <div className="space-y-4">
        {/* Tech Stack Selector */}
        <TechStackSelector
          technicalSolution={technicalSolution}
          onChange={setTechnicalSolution}
          isGenerating={false}
          defaultExpanded={hasAdvancedContent}
        />

        {/* Architecture Visualizer */}
        <ArchitectureVisualizer
          architecture={technicalSolution.architecture}
          onChange={(architecture) =>
            setTechnicalSolution({ ...technicalSolution, architecture })
          }
        />

        {/* Technical Considerations */}
        <TechnicalConsiderations
          considerations={technicalSolution.considerations}
          onChange={(considerations) =>
            setTechnicalSolution({ ...technicalSolution, considerations })
          }
        />

        {/* Complexity Indicator */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Complexidade do Desenvolvimento
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Geral</label>
              <select
                value={technicalSolution.complexity?.overall}
                onChange={(e) =>
                  setTechnicalSolution({
                    ...technicalSolution,
                    complexity: {
                      ...technicalSolution.complexity,
                      overall: e.target.value as "low" | "medium" | "high",
                    },
                  })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="low">Baixa</option>
                <option value="medium">Média</option>
                <option value="high">Alta</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Frontend (1-10)</label>
              <input
                type="number"
                min="1"
                max="10"
                value={technicalSolution.complexity?.frontend}
                onChange={(e) =>
                  setTechnicalSolution({
                    ...technicalSolution,
                    complexity: {
                      ...technicalSolution.complexity,
                      frontend: parseInt(e.target.value) || 5,
                    },
                  })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Backend (1-10)</label>
              <input
                type="number"
                min="1"
                max="10"
                value={technicalSolution.complexity?.backend}
                onChange={(e) =>
                  setTechnicalSolution({
                    ...technicalSolution,
                    complexity: {
                      ...technicalSolution.complexity,
                      backend: parseInt(e.target.value) || 5,
                    },
                  })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">DevOps (1-10)</label>
              <input
                type="number"
                min="1"
                max="10"
                value={technicalSolution.complexity?.devops}
                onChange={(e) =>
                  setTechnicalSolution({
                    ...technicalSolution,
                    complexity: {
                      ...technicalSolution.complexity,
                      devops: parseInt(e.target.value) || 5,
                    },
                  })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Technical Insights */}
      <TechnicalInsights technicalSolution={technicalSolution} />

      {/* Action Buttons - Fixed at bottom */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sticky bottom-4 shadow-lg">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">
            Alterações são salvas automaticamente
          </span>
          
          <Button
            onClick={() => handleSaveAdvancedSolution(technicalSolution)}
            className="px-6"
          >
            Salvar e Continuar
          </Button>
        </div>
      </div>
    </div>
  );
}